import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

export default function Step3Requirements({ programId, formData, onBack, onSuccess, addToast }) {
  const [requirements, setRequirements] = useState([]);
  const [files, setFiles] = useState({});
  const [dragging, setDragging] = useState(null); // reqId del que está activo
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!programId) return;
    setLoading(true);
    api.getRequirements(programId)
      .then((data) => setRequirements(Array.isArray(data) ? data : []))
      .catch(() => addToast('error', 'No se pudo cargar los requisitos.'))
      .finally(() => setLoading(false));
  }, [programId]);

  const handleFile = (reqId, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      addToast('warning', 'Solo se aceptan archivos PDF.');
      return;
    }
    setFiles((prev) => ({ ...prev, [reqId]: file }));
  };

  const handleDragOver = (e, reqId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(reqId);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(null);
  };

  const handleDrop = (e, reqId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(null);
    const file = e.dataTransfer.files[0];
    handleFile(reqId, file);
  };

  const removeFile = (reqId) => {
    setFiles((prev) => { const n = { ...prev }; delete n[reqId]; return n; });
  };

  const handleSubmit = async () => {
    if (!terms) { addToast('warning', 'Debe aceptar los términos y condiciones.'); return; }

    // Validar que todos los requisitos tengan archivo adjunto
    const missing = requirements.filter((req) => !files[req.id]);
    if (missing.length > 0) {
      addToast('warning', `Debe adjuntar todos los documentos. Faltan: ${missing.length} archivo(s).`);
      return;
    }

    const app = formData;

    // Validar campos críticos antes de enviar
    if (!app.docType || !app.docNumber || !app.detailId) {
      addToast('error', 'Faltan datos del proceso o del documento. Regrese al paso anterior.');
      return;
    }

    setSubmitting(true);

    // Verificar registro duplicado antes de enviar
    const check = await api.checkDuplicate(app.docNumber, app.detailId);
    if (check && (check.exists || check.registered || check.found)) {
      addToast('warning', 'Ya existe una inscripción con su número de documento para este proceso. Solo se permite una postulación por proceso.');
      setSubmitting(false);
      return;
    }

    const fd = new FormData();

    fd.append('id_document_type',            app.docType);
    fd.append('document_number',             String(app.docNumber || '').trim());
    fd.append('name',                        String(app.name      || '').trim());
    fd.append('paternal',                    String(app.paternal  || '').trim());
    fd.append('maternal',                    String(app.maternal  || '').trim());
    fd.append('gender',                      String(app.gender    || ''));
    fd.append('birth_date',                  String(app.birthDate || ''));
    fd.append('cell_phone',                  String(app.cell      || '').trim());
    fd.append('email',                       String(app.email     || '').trim());
    fd.append('address',                     String(app.address   || '').trim());
    fd.append('id_admission_process_detail', app.detailId);
    fd.append('application_date',            new Date().toISOString().split('T')[0]);
    fd.append('state',                       'pendiente');
    if (app.ubigeoBirthId)    fd.append('id_ubigeo_birth',   app.ubigeoBirthId);
    if (app.ubigeoResidentId) fd.append('id_ubigeo',         app.ubigeoResidentId);
    if (app.costId)           fd.append('id_cost_admission', app.costId);

    Object.entries(files).forEach(([reqId, file]) => fd.append(`file_req_${reqId}`, file));

    try {
      const res = await api.register(fd);
      const code = res.code || res.codigo || res.id || 'SIN-CODIGO';
      onSuccess(`POSTULANTE-${code}`);
    } catch (err) {
      const msg = (err.message || '').toLowerCase();
      if (msg.includes('ya exist') || msg.includes('duplicate') || msg.includes('registrado') || msg.includes('única') || msg.includes('unica') || msg.includes('409')) {
        addToast('error', 'Ya existe una inscripción con su documento para este proceso. Solo se permite una postulación por proceso.');
      } else {
        addToast('error', err.message || 'Error al registrar la inscripción. Intente nuevamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="card">
      <div className="loading-box">Cargando requisitos...</div>
    </div>
  );

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-icon">📎</div>
        <div>
          <div className="card-title">Requisitos Documentales</div>
          <div className="card-sub">Adjunte los documentos requeridos en formato PDF</div>
        </div>
      </div>

      {requirements.length === 0 ? (
        <div className="req-empty">No hay requisitos registrados para este programa.</div>
      ) : (
        <div className="req-list">
          {requirements.map((req, i) => {
            const r = req.Requeriment || {};
            const urlFormato  = r.url_formato  || '';
            const urlVideo    = r.url_video    || '';
            const urlDescarga = r.descargar_formato && r.formato ? r.formato : '';
            const hasFile = !!files[req.id];
            return (
            <div key={req.id} className={`req-item ${hasFile ? 'req-done' : 'req-missing'}`}>
              <div className="req-num">{i + 1}</div>
              <div className="req-info">
                <div className="req-name">
                  {r.description || `Requisito ${i + 1}`}
                </div>
                {r.type_entry && (
                  <div className="req-type">{r.type_entry}</div>
                )}
                <div className="req-links">
                  {urlFormato  && <a href={urlFormato}  target="_blank" rel="noreferrer">📄 Ver formato</a>}
                  {urlVideo    && <a href={urlVideo}    target="_blank" rel="noreferrer">🎥 Ver video</a>}
                  {urlDescarga && <a href={urlDescarga} target="_blank" rel="noreferrer">⬇ Descargar</a>}
                </div>
              </div>
              <div
                className={`drop-zone${files[req.id] ? ' drop-zone--done' : ''}${dragging === req.id ? ' drop-zone--over' : ''}`}
                onDragOver={(e) => handleDragOver(e, req.id)}
                onDragEnter={(e) => handleDragOver(e, req.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, req.id)}
              >
                {files[req.id] ? (
                  <div className="drop-zone__file">
                    <span className="drop-zone__icon">📄</span>
                    <span className="drop-zone__name">{files[req.id].name}</span>
                    <button type="button" className="drop-zone__remove" onClick={() => removeFile(req.id)} title="Quitar archivo">✕</button>
                  </div>
                ) : (
                  <label className="drop-zone__label">
                    <span className="drop-zone__icon">{dragging === req.id ? '📂' : '⬆'}</span>
                    <span className="drop-zone__text">
                      {dragging === req.id ? 'Suelta aquí' : 'Arrastra el PDF o haz clic'}
                    </span>
                    <input
                      type="file"
                      accept="application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFile(req.id, e.target.files[0])}
                    />
                  </label>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      <div className="terms-box">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
          />
          <span>
            Declaro que la información proporcionada es verídica y acepto los{' '}
            <a href="#" onClick={(e) => e.preventDefault()}>términos y condiciones</a>{' '}
            del proceso de admisión EPG–UNSM.
          </span>
        </label>
      </div>

      <div className="step-actions">
        <button className="btn-ghost" onClick={onBack} disabled={submitting}>← Atrás</button>
        <button className="btn-success" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Registrando...' : '✅ Registrar Inscripción'}
        </button>
      </div>
    </div>
  );
}
